import { useState, useEffect, useMemo } from "react";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";
import { feature } from "topojson-client";

const WORLD_URL = "/world-countries.json";
const INDIA_URL = "/india-states.geojson";
const W = 800;
const H = 500;

const landStyle = {
  default: { fill: "rgba(12,50,68,0.07)", stroke: "#0C3244", strokeWidth: 0.3, strokeOpacity: 0.25, outline: "none" },
  hover: { fill: "rgba(12,50,68,0.07)", outline: "none" },
  pressed: { fill: "rgba(12,50,68,0.07)", outline: "none" },
};

export default function JourneyRouteMap({ route = [] }) {
  const [worldGeo, setWorldGeo] = useState(null);
  const [indiaGeo, setIndiaGeo] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(WORLD_URL).then((r) => r.json()).then((topo) => {
      if (!alive) return;
      const geo = feature(topo, topo.objects.countries);
      // India always drawn from the Foundation's authoritative GeoJSON
      setWorldGeo({ ...geo, features: geo.features.filter((f) => f.properties.name !== "Antarctica" && f.properties.name !== "India") });
    });
    fetch(INDIA_URL).then((r) => r.json()).then((geo) => { if (alive) setIndiaGeo(geo); });
    return () => { alive = false; };
  }, []);

  const { center, scale, uniqueStops, offMap } = useMemo(() => {
    const med = (arr) => [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)];
    const medLat = med(route.map((s) => s.lat));
    const medLng = med(route.map((s) => s.lng));
    // Zoom to the core cluster; far-away gateway cities (e.g. the home airport) go off-map
    const core = route.filter((s) => Math.abs(s.lat - medLat) <= 10 && Math.abs(s.lng - medLng) <= 10);
    const fit = core.length >= 2 ? core : route;
    const lats = fit.map((s) => s.lat);
    const lngs = fit.map((s) => s.lng);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const latSpan = Math.max(maxLat - minLat, 1.5);
    const lngSpan = Math.max(maxLng - minLng, 1.5);
    const rad = Math.PI / 180;
    const s = Math.min((W * 0.6) / (lngSpan * rad), (H * 0.52) / (latSpan * rad));
    const coreNames = new Set(fit.map((x) => x.name));
    const seen = new Set();
    const uniq = [];
    route.forEach((st) => {
      if (coreNames.has(st.name) && !seen.has(st.name)) { seen.add(st.name); uniq.push({ ...st, order: uniq.length + 1 }); }
    });
    const off = [...new Set(route.filter((x) => !coreNames.has(x.name)).map((x) => x.name))];
    return {
      center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
      scale: Math.max(120, Math.min(s, 3600)),
      uniqueStops: uniq,
      offMap: off,
    };
  }, [route]);

  if (route.length < 2) return null;

  return (
    <div data-testid="journey-route-map" className="border border-ptf-primary/12 bg-white mx-auto md:max-w-[850px]">
      <ComposableMap projection="geoMercator" projectionConfig={{ center, scale }} width={W} height={H} style={{ width: "100%", height: "auto" }}>
        {worldGeo && (
          <Geographies geography={worldGeo}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} style={landStyle} tabIndex={-1} />
            ))}
          </Geographies>
        )}
        {indiaGeo && (
          <Geographies geography={indiaGeo}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} style={landStyle} tabIndex={-1} />
            ))}
          </Geographies>
        )}
        {route.slice(1).map((stop, i) => {
          const prev = route[i];
          const fly = stop.mode === "fly";
          return (
            <Line
              key={`leg-${i}`}
              from={[prev.lng, prev.lat]}
              to={[stop.lng, stop.lat]}
              stroke={fly ? "#1B6E8C" : "#0C3244"}
              strokeWidth={fly ? 1.4 : 2}
              strokeDasharray={fly ? "5 4" : "none"}
              strokeLinecap="round"
            />
          );
        })}
        {uniqueStops.map((stop, i) => {
          const pos = i % 4;
          const label = pos === 0
            ? { anchor: "middle", x: 0, y: -16 }
            : pos === 1
              ? { anchor: "middle", x: 0, y: 26 }
              : pos === 2
                ? { anchor: "end", x: -14, y: 4 }
                : { anchor: "start", x: 14, y: 4 };
          return (
            <Marker key={stop.name} coordinates={[stop.lng, stop.lat]}>
              <circle r={9} fill="#0C3244" />
              <text textAnchor="middle" y={3.5} style={{ fill: "#F4EFE4", fontSize: "9px", fontWeight: 700, fontFamily: "sans-serif" }}>{stop.order}</text>
              <text
                textAnchor={label.anchor}
                x={label.x}
                y={label.y}
                style={{ fill: "#0C3244", fontSize: "12px", fontWeight: 600, fontFamily: "sans-serif", letterSpacing: "0.04em", paintOrder: "stroke", stroke: "#F4EFE4", strokeWidth: 3 }}
              >
                {stop.name}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-ptf-primary/10 px-5 py-3">
        <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/60">
          <svg width="26" height="4"><line x1="0" y1="2" x2="26" y2="2" stroke="#0C3244" strokeWidth="2.5" strokeLinecap="round" /></svg>
          Overland
        </span>
        <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/60">
          <svg width="26" height="4"><line x1="0" y1="2" x2="26" y2="2" stroke="#1B6E8C" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" /></svg>
          By air
        </span>
        <span className="ml-auto text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/45">
          {offMap.length > 0 ? `Starts & ends: ${offMap.join(", ")} · ` : ""}{uniqueStops.length} stops
        </span>
      </div>
    </div>
  );
}
